/**
 * Alert dot on the account button. Replaces `alert-status.js` which scanned
 * the DOM looking at banner background colours to decide whether to show the
 * dot. The new implementation queries the status services directly — no DOM
 * heuristics, no MutationObservers fighting the user.
 *
 * Public API:
 *   - `refresh()`            — recompute + repaint the dot.
 *   - `init()`               — wire bus listeners so the dot auto-updates.
 */

import { LocalState } from '../../core/localState.js';
import * as bus from '../../core/eventBus.js';
import { createLogger } from '../../core/logger.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { getStatus as getLicenseStatus } from './license.service.js';
import { getLastOwnership, getStoredOwnershipISO } from './ownership.service.js';
import { isoToYearMonth } from '../../shared/date.js';

const log = createLogger('alert-status');

const ACCOUNT_BTN_ID = 'homeProfileBtn';
// Distinct class from `.notification-dot` so we don't accidentally remove the
// unread-message badge (#headerNotificationBadge) when toggling the license
// warning indicator.
const DOT_CLASS = 'license-alert-dot';

let wired = false;

/** Wire global listeners so the dot updates automatically. Idempotent.
 *
 * Important: we do NOT subscribe to `license:status` here because `refresh()`
 * itself emits that event — listening to it would create an infinite loop.
 * License re-evaluation happens implicitly through `vehicle:changed` and
 * `auth:ready` triggers, which is enough in practice.
 */
export function init() {
  if (wired) return;
  wired = true;
  bus.on('vehicle:changed', () => { void refresh(); });
  bus.on('auth:ready', () => { void refresh(); });
  bus.on('auth:logged-out', () => setDot(false));
}

/**
 * Recompute the alert state and repaint the dot. Safe to call repeatedly.
 * @returns {Promise<boolean>}  whether the dot is now visible
 */
export async function refresh() {
  try {
    const plate = normalizePlate(LocalState.plate ?? '');
    if (!plate) { setDot(false); return false; }

    const [license, govOwn, fbOwnISO] = await Promise.all([
      getLicenseStatus(plate),
      getLastOwnership(plate),
      getStoredOwnershipISO(plate),
    ]);

    const licenseAlert = license.status === 'red' || license.status === 'orange';
    const govMonth = govOwn.yearMonth;
    const fbMonth = isoToYearMonth(fbOwnISO);
    const ownershipMismatch = !!(govMonth && fbMonth && govMonth !== fbMonth);

    const showDot = licenseAlert || ownershipMismatch;
    setDot(showDot);

    // Persist the unified state so legacy surfaces (notably `card_myCar` on
    // the home feed) can render the same "✅ הכול תקין / ⚠️ דורש תשומת לב"
    // verdict the drawer-side banners show. Without this the home card
    // checks only the ownership-mismatch flag and says "תקין" while the
    // banner inside the drawer is screaming about license expiry.
    try {
      localStorage.setItem(`c2c_alert_state_${plate}`, JSON.stringify({
        license: license.status,
        ownershipMismatch,
        hasAlert: showDot,
        at: Date.now(),
      }));
    } catch { /* quota / disabled */ }

    bus.emit('license:status', { plate, status: license.status === 'unknown' ? 'orange' : license.status });
    return showDot;
  } catch (err) {
    log.warn('refresh failed:', err);
    return false;
  }
}

// ====================================================================
// internals
// ====================================================================

/** @param {boolean} show */
function setDot(show) {
  const btn = document.getElementById(ACCOUNT_BTN_ID);
  if (!btn) return;
  btn.querySelectorAll(`.${DOT_CLASS}`).forEach((d) => d.remove());
  if (!show) return;

  const rect = btn.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  // IMPORTANT: do NOT set btn.style.position = 'relative' here. The legacy
  // version did, which overrode the CSS that absolute-positions the profile
  // button to the right side of the header. The button is already its own
  // positioning context (position: absolute), so absolutely-positioned children
  // anchor to it without help. See the bug fixed on 2026-05-16.
  const dot = document.createElement('div');
  dot.className = DOT_CLASS;
  dot.setAttribute('aria-label', 'יש התראות חדשות');
  dot.textContent = '⚠️';
  // Anchor to the bottom-start corner of the profile button so it doesn't
  // collide with the unread-count badge which sits at top-end.
  dot.style.cssText = `
    position:absolute !important;
    bottom:-6px !important;
    inset-inline-start:-6px !important;
    font-size:16px !important;
    line-height:1 !important;
    pointer-events:none !important;
    z-index:5 !important;
  `;
  btn.appendChild(dot);
}
